import { Language } from '../types';

export interface RouteDefinition {
  view: string;
  path: string;
  aliases: string[];
}

export const ROUTES: RouteDefinition[] = [
  {
    view: 'home',
    path: '/',
    aliases: ['/home', '/accueil']
  },
  {
    view: 'services',
    path: '/services',
    aliases: ['/nos-services']
  },
  {
    view: 'service-plumbing',
    path: '/services/plomberie',
    aliases: ['/plomberie', '/plombier', '/plombier-bruxelles', '/service-plumbing']
  },
  {
    view: 'service-heating',
    path: '/services/chauffage',
    aliases: ['/chauffage', '/chauffagiste', '/entretien-chaudiere', '/service-heating']
  },
  {
    view: 'service-unblocking',
    path: '/services/debouchage',
    aliases: ['/debouchage', '/debouchage-urgent', '/debouchage-canalisation', '/service-unblocking']
  },
  {
    view: 'service-ventilation',
    path: '/services/ventilation',
    aliases: ['/ventilation', '/vmc', '/service-ventilation']
  },
  {
    view: 'service-water-softener',
    path: '/services/adoucisseur',
    aliases: ['/adoucisseur', '/adoucisseur-eau', '/waterontharder', '/service-water-softener']
  },
  {
    view: 'zones',
    path: '/zones-intervention',
    aliases: ['/zones', '/zones-d-intervention', '/interventions', '/belgique']
  },
  {
    view: 'zone-bruxelles',
    path: '/zones/bruxelles',
    aliases: ['/zones/bruxelles-1000', '/bruxelles', '/plombier-bruxelles']
  },
  {
    view: 'zone-sint-pieters-leeuw',
    path: '/zones/sint-pieters-leeuw',
    aliases: ['/zones/leeuw', '/sint-pieters-leeuw', '/loodgieter-sint-pieters-leeuw']
  },
  {
    view: 'zone-ixelles',
    path: '/zones/ixelles',
    aliases: ['/zones/elsene', '/ixelles', '/elsene']
  },
  {
    view: 'zone-uccle',
    path: '/zones/uccle',
    aliases: ['/zones/ukkel', '/uccle', '/ukkel']
  },
  {
    view: 'zone-anderlecht',
    path: '/zones/anderlecht',
    aliases: ['/anderlecht']
  },
  {
    view: 'zone-waterloo',
    path: '/zones/waterloo',
    aliases: ['/waterloo']
  },
  {
    view: 'zone-bruxelles-19-communes',
    path: '/zones/bruxelles-19-communes',
    aliases: ['/zones/region-bruxelles', '/bruxelles-19-communes', '/region-bruxelles']
  },
  {
    view: 'zone-brabant-flamand',
    path: '/zones/brabant-flamand',
    aliases: ['/zones/vlaams-brabant', '/vlaams-brabant', '/brabant-flamand']
  },
  {
    view: 'zone-brabant-wallon',
    path: '/zones/brabant-wallon',
    aliases: ['/zones/waals-brabant', '/waals-brabant', '/brabant-wallon']
  },
  {
    view: 'about',
    path: '/a-propos',
    aliases: ['/about', '/qui-sommes-nous']
  },
  {
    view: 'contact',
    path: '/contact',
    aliases: ['/devis', '/devis-gratuit', '/contact-devis']
  },
  {
    view: 'admin',
    path: '/admin',
    aliases: ['/backoffice', '/dashboard']
  }
];

/**
 * Returns the clean SEO URL path for a given internal view name
 */
export function getPathForView(view: string, lang?: Language): string {
  const match = ROUTES.find((r) => r.view === view);
  let basePath = match ? match.path : '/';
  if (!match && view.startsWith('zone-')) {
    basePath = `/zones/${view.replace('zone-', '')}`;
  }

  if (lang && lang !== 'fr') {
    return `${basePath}?lang=${lang}`;
  }
  return basePath;
}

/**
 * Resolves the view name and language from the current window location (pathname and search)
 */
export function resolveRoute(
  pathname: string = window.location.pathname,
  search: string = window.location.search
): { view: string; lang: Language; shouldReplaceUrl: boolean; targetPath: string } {
  const params = new URLSearchParams(search);
  const langParam = params.get('lang');
  const validLang: Language = (langParam === 'nl' || langParam === 'en') ? langParam : 'fr';

  // 1. Check legacy query parameter `?view=...`
  const legacyViewParam = params.get('view');
  if (legacyViewParam) {
    const targetRoute = ROUTES.find((r) => r.view === legacyViewParam || r.aliases.includes(`/${legacyViewParam}`));
    const cleanView = targetRoute ? targetRoute.view : (ROUTES.some(r => r.view === legacyViewParam) ? legacyViewParam : 'home');
    const targetPath = getPathForView(cleanView, validLang);
    return {
      view: cleanView,
      lang: validLang,
      shouldReplaceUrl: true,
      targetPath
    };
  }

  // 2. Normalize pathname (strip trailing slash except for root)
  const normalizedPath = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  // 3. Match against clean paths and aliases
  for (const route of ROUTES) {
    if (route.path === normalizedPath || route.aliases.includes(normalizedPath)) {
      const isCanonicalPath = route.path === normalizedPath;
      const targetPath = getPathForView(route.view, validLang);
      return {
        view: route.view,
        lang: validLang,
        shouldReplaceUrl: !isCanonicalPath,
        targetPath
      };
    }
  }

  // 4. Dynamic match for /zones/:slug
  if (normalizedPath.startsWith('/zones/')) {
    const slug = normalizedPath.replace('/zones/', '');
    if (slug) {
      const dynamicView = `zone-${slug}`;
      const targetPath = getPathForView(dynamicView, validLang);
      return {
        view: dynamicView,
        lang: validLang,
        shouldReplaceUrl: false,
        targetPath
      };
    }
  }

  // Fallback to home
  return {
    view: 'home',
    lang: validLang,
    shouldReplaceUrl: false,
    targetPath: '/'
  };
}

/**
 * Client-side programmatic navigation preserving browser history and clean SEO URLs
 */
export function navigate(view: string, lang?: Language) {
  const cleanPath = getPathForView(view, lang);
  if (window.location.pathname + window.location.search !== cleanPath) {
    window.history.pushState({ view, lang }, '', cleanPath);
  }
  window.dispatchEvent(new CustomEvent('appNavigate', { detail: { view, lang } }));
}
